/**
 * ═══ LE NOM D'UNE LANGUE, DANS CETTE LANGUE ═══
 *
 * Le sélecteur affichait « FR », « EN », « IT » : la clé technique de la base, celle qui
 * sert à ranger les traductions. Personne ne choisit sa langue dans un code à deux
 * lettres, et surtout pas quelqu'un qui ne lit pas celle affichée.
 *
 * ⚠️ CHAQUE LANGUE EST NOMMÉE DANS SA PROPRE LANGUE, jamais traduite dans celle qu'on
 * lit. C'est la convention de tous les sélecteurs sérieux, et elle a une raison : le
 * lecteur chinois cherche « 中文 », pas « Chinois ». Traduire la liste rendrait toutes ses
 * entrées illisibles à ceux à qui elles s'adressent.
 *
 * ⚠️ LES NOMS VIENNENT D'`Intl`, PAS D'UNE TABLE À NOUS. La base porte bien un libellé
 * (`Languages.label`), mais il est écrit dans des langues qui changent d'une ligne à
 * l'autre : « Français », « Italiano », mais aussi « Espagnol », « Chinese », « German ».
 * Une liste où trois entrées sur six sont dans la mauvaise langue est exactement le
 * défaut qu'on corrige ici. `Intl` les connaît toutes, sans rien à maintenir, et une
 * langue ajoutée demain sera nommée juste sans qu'on y touche.
 */

/**
 * ⚠️ LA CLÉ N'EST PAS TOUJOURS UN CODE DE LANGUE. La base range le chinois sous « CN »,
 * qui est un code de PAYS : `Intl` n'en tire rien. Les autres se trouvent être valides.
 */
const CODES: Record<string, string> = { CN: "zh" };

/**
 * LE DRAPEAU, PARCE QU'ON LE REPÈRE AVANT DE LIRE.
 *
 * ⚠️ IL SUIT LA CLÉ DE LA BASE, ET C'EST CE QUI LE REND LÉGITIME ICI : ce sont exactement
 * ceux que le produit porte déjà dans ses libellés (« Français  🇫🇷 »), donc on ne décide
 * rien de nouveau. Un drapeau n'est pas une langue — l'anglais n'est pas britannique, le
 * chinois n'est pas que de Chine — mais c'est le choix du produit, pas le nôtre.
 *
 * Une langue absente de cette table s'affiche sans drapeau plutôt qu'avec un mauvais.
 */
const FLAGS: Record<string, string> = {
  FR: "🇫🇷",
  EN: "🇬🇧",
  IT: "🇮🇹",
  ES: "🇪🇸",
  DE: "🇩🇪",
  CN: "🇨🇳",
};

/** La première lettre en capitale, sans couper une lettre composée en deux. */
function capitalised(word: string, locale: string): string {
  const [first, ...remaining] = [...word];
  return first ? first.toLocaleUpperCase(locale) + remaining.join("") : word;
}

/**
 * CE QU'ON AFFICHE POUR UNE LANGUE : « 🇫🇷 Français ».
 *
 * ⚠️ AVEC UN REPLI QUI VIENT DE LA BASE, et il est indispensable : `Intl` peut ne pas
 * connaître une clé (une langue ajoutée sous un code inattendu), et une entrée vide dans
 * un sélecteur ne se choisit pas. Le libellé du produit, même imparfait, reste lisible.
 */
export function languageName(key: string, fallback: string): string {
  const flag = FLAGS[key.toUpperCase()];
  const code = CODES[key.toUpperCase()] ?? key.toLowerCase();

  let name = fallback;
  try {
    const say = new Intl.DisplayNames([code], { type: "language" }).of(code);
    /* `of` rend le code lui-même quand il ne sait pas : c'est un refus, pas un nom, et
       l'afficher rendrait « zh » au lieu de « 中文 ». */
    if (say && say.toLowerCase() !== code.toLowerCase()) name = capitalised(say, code);
  } catch {
    /* Étiquette que l'environnement refuse : on garde le libellé de la base plutôt que
       de faire tomber l'en-tête du site pour un nom de langue. */
  }

  return flag ? `${flag} ${name}` : name;
}
